/**
 * useAdminData - Custom hook for admin data fetching with caching
 * 
 * Provides optimized data fetching with:
 * - Client-side caching
 * - Loading states
 * - Error handling
 * - Automatic refresh
 * - Stale-while-revalidate pattern
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import AdminCache from './AdminCache';

/**
 * Custom hook for fetching admin data with caching
 * 
 * @param {string} cacheKey - Unique cache key for this data
 * @param {string} url - API endpoint URL
 * @param {object} options - Configuration options
 * @param {number} options.ttl - Cache TTL in milliseconds
 * @param {boolean} options.enabled - Whether to fetch data
 * @param {object} options.params - Query parameters
 * @param {Function} options.transform - Transform function for response data
 * @param {any} options.fallbackData - Fallback data if fetch fails
 * @param {boolean} options.staleWhileRevalidate - Return stale data while fetching fresh
 * @returns {object} - { data, loading, error, refresh, fromCache }
 */
export const useAdminData = (cacheKey, url, options = {}) => {
  const {
    ttl = AdminCache.TTL.medium,
    enabled = true,
    params = {},
    transform = (data) => data,
    fallbackData = null,
    staleWhileRevalidate = true
  } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fromCache, setFromCache] = useState(false);
  const abortControllerRef = useRef(null);

  // RC1: Refs to access fromCache and data inside fetchData without adding them to deps
  const fromCacheRef = useRef(false);
  const dataRef = useRef(null);
  fromCacheRef.current = fromCache;
  dataRef.current = data;

  // Stabilize transform and fallbackData via refs so they don't cause fetchData recreation
  const transformRef = useRef(transform);
  transformRef.current = transform;
  const fallbackDataRef = useRef(fallbackData);
  fallbackDataRef.current = fallbackData;

  // RC2: Stabilize params reference using JSON.stringify comparison
  const paramsString = JSON.stringify(params);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableParams = useMemo(() => params, [paramsString]);

  // Build full cache key with stable params
  const fullCacheKey = AdminCache.createKey(cacheKey, stableParams);

  // Build URL with stable params
  const buildUrl = useCallback(() => {
    const urlObj = new URL(url);
    Object.entries(stableParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        urlObj.searchParams.set(key, value);
      }
    });
    return urlObj.toString();
  }, [url, stableParams]);

  // Fetch data function
  const fetchData = useCallback(async (skipCache = false) => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    // Check cache first (unless skipCache is true)
    if (!skipCache) {
      const cached = AdminCache.get(fullCacheKey);
      if (cached !== null) {
        setData(transformRef.current(cached));
        setFromCache(true);
        setLoading(false);
        
        // If staleWhileRevalidate, still fetch fresh data in background
        if (!staleWhileRevalidate) {
          return;
        }
      }
    }

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      if (!fromCacheRef.current) {
        setLoading(true);
      }
      setError(null);

      const session = await fetchAuthSession();
      // CRITICAL: Use idToken (not accessToken) for admin authorization
      // cognito:groups claim is ONLY in ID token, NOT in access token
      // Lambda functions need to check groups for admin access
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(buildUrl(), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        if (response.status === 401) throw new Error('Unauthorized - Admin access required');
        if (response.status === 403) throw new Error('Forbidden - Insufficient permissions');
        throw new Error(`Request failed: ${response.status}`);
      }

      const responseData = await response.json();
      
      // Cache the response
      AdminCache.set(fullCacheKey, responseData, ttl);
      
      // Transform and set data
      setData(transformRef.current(responseData));
      setFromCache(false);
      setError(null);
    } catch (err) {
      // Ignore abort errors
      if (err.name === 'AbortError') {
        return;
      }

      console.error(`[useAdminData] Error fetching ${cacheKey}:`, err);
      setError(err.message);

      // Use fallback data if provided and no cached data
      if (fallbackDataRef.current && !dataRef.current) {
        setData(fallbackDataRef.current);
      }
    } finally {
      setLoading(false);
    }
  }, [enabled, fullCacheKey, buildUrl, ttl, staleWhileRevalidate]);

  // Initial fetch
  useEffect(() => {
    fetchData();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fullCacheKey, enabled]);

  // Refresh function (bypasses cache)
  const refresh = useCallback(() => {
    AdminCache.remove(fullCacheKey);
    return fetchData(true);
  }, [fullCacheKey, fetchData]);

  return {
    data,
    loading,
    error,
    refresh,
    refetch: refresh,
    fromCache
  };
};

/**
 * Hook for fetching multiple admin data sources in parallel
 * 
 * @param {Array} sources - Array of { cacheKey, url, options }
 * @returns {object} - { data: {}, loading, errors: {}, refresh }
 */
export const useAdminDataMultiple = (sources) => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});

  const fetchAll = useCallback(async (skipCache = false) => {
    setLoading(true);
    const results = {};
    const newErrors = {};

    await Promise.all(
      sources.map(async ({ cacheKey, url, options = {} }) => {
        const fullCacheKey = AdminCache.createKey(cacheKey, options.params || {});
        
        // Check cache first
        if (!skipCache) {
          const cached = AdminCache.get(fullCacheKey);
          if (cached !== null) {
            results[cacheKey] = options.transform ? options.transform(cached) : cached;
            return;
          }
        }

        try {
          const session = await fetchAuthSession();
          // CRITICAL: Use idToken (not accessToken) for admin authorization
          // cognito:groups claim is ONLY in ID token, NOT in access token
          // Lambda functions need to check groups for admin access
          const token = session.tokens?.idToken?.toString();

          if (!token) {
            throw new Error('Not authenticated');
          }

          const urlObj = new URL(url);
          Object.entries(options.params || {}).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              urlObj.searchParams.set(key, value);
            }
          });

          const response = await fetch(urlObj.toString(), {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (!response.ok) {
            throw new Error(`Request failed: ${response.status}`);
          }

          const responseData = await response.json();
          AdminCache.set(fullCacheKey, responseData, options.ttl || AdminCache.TTL.medium);
          results[cacheKey] = options.transform ? options.transform(responseData) : responseData;
        } catch (err) {
          console.error(`[useAdminDataMultiple] Error fetching ${cacheKey}:`, err);
          newErrors[cacheKey] = err.message;
          
          if (options.fallbackData) {
            results[cacheKey] = options.fallbackData;
          }
        }
      })
    );

    setData(results);
    setErrors(newErrors);
    setLoading(false);
  }, [sources]);

  useEffect(() => {
    fetchAll();
  }, []);

  const refresh = useCallback(() => {
    sources.forEach(({ cacheKey, options = {} }) => {
      const fullCacheKey = AdminCache.createKey(cacheKey, options.params || {});
      AdminCache.remove(fullCacheKey);
    });
    return fetchAll(true);
  }, [sources, fetchAll]);

  return {
    data,
    loading,
    errors,
    refresh
  };
};

/**
 * Hook for lazy loading data (only fetches when triggered)
 * 
 * @param {string} cacheKey - Cache key
 * @param {string} url - API endpoint
 * @param {object} options - Options
 * @returns {object} - { data, loading, error, fetch }
 */
export const useAdminDataLazy = (cacheKey, url, options = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (params = {}) => {
    const fullCacheKey = AdminCache.createKey(cacheKey, params);
    
    // Check cache first
    const cached = AdminCache.get(fullCacheKey);
    if (cached !== null) {
      setData(options.transform ? options.transform(cached) : cached);
      return cached;
    }

    setLoading(true);
    setError(null);

    try {
      const session = await fetchAuthSession();
      // CRITICAL: Use idToken (not accessToken) for admin authorization
      // cognito:groups claim is ONLY in ID token, NOT in access token
      // Lambda functions need to check groups for admin access
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        throw new Error('Not authenticated');
      }

      const urlObj = new URL(url);
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          urlObj.searchParams.set(key, value);
        }
      });

      const response = await fetch(urlObj.toString(), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      const responseData = await response.json();
      AdminCache.set(fullCacheKey, responseData, options.ttl || AdminCache.TTL.medium);
      
      const transformed = options.transform ? options.transform(responseData) : responseData;
      setData(transformed);
      return transformed;
    } catch (err) {
      console.error(`[useAdminDataLazy] Error:`, err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [cacheKey, url, options]);

  return {
    data,
    loading,
    error,
    fetch: fetchData
  };
};

export default useAdminData;
